import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "tti_currency";

const FALLBACK = {
  currencies: [
    { code: "USD", name: "US Dollar", symbol: "$", flag: "us" },
    { code: "INR", name: "Indian Rupee", symbol: "₹", flag: "in" },
    { code: "EUR", name: "Euro", symbol: "€", flag: "eu" },
    { code: "GBP", name: "Pound Sterling", symbol: "£", flag: "gb" },
    { code: "AUD", name: "Australian Dollar", symbol: "A$", flag: "au" },
    { code: "CAD", name: "Canadian Dollar", symbol: "C$", flag: "ca" },
    { code: "JPY", name: "Japanese Yen", symbol: "¥", flag: "jp" },
    { code: "AED", name: "UAE Dirham", symbol: "د.إ", flag: "ae" },
    { code: "SGD", name: "Singapore Dollar", symbol: "S$", flag: "sg" },
  ],
  rates: { USD: 1, INR: 83.2, EUR: 0.92, GBP: 0.79, AUD: 1.52, CAD: 1.36, JPY: 156, AED: 3.67, SGD: 1.34 },
};

const CurrencyContext = createContext({
  currency: FALLBACK.currencies[0],
  setCurrency: () => {},
  currencies: FALLBACK.currencies,
  rates: FALLBACK.rates,
  format: (usd) => `$${usd}`,
  convert: (usd) => usd,
});

export function CurrencyProvider({ children }) {
  const [data, setData] = useState(FALLBACK);
  const [currency, _setCurrency] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const c = JSON.parse(saved);
        if (c && c.code) return c;
      }
    } catch (e) {
      console.warn("[Currency] failed to parse saved currency preference:", e?.message || e);
    }
    return FALLBACK.currencies[0];
  });

  useEffect(() => {
    api
      .get("/currencies")
      .then((r) => {
        if (r.data?.currencies?.length && r.data?.rates) setData(r.data);
      })
      .catch((e) => {
        // Non-fatal: Frankfurter API may be unreachable in this container — we use static FALLBACK rates.
        console.warn("[Currency] live rates unavailable, using static fallback:", e?.message || e);
      });
  }, []);

  const setCurrency = (c) => {
    _setCurrency(c);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    } catch (e) {
      console.warn("[Currency] localStorage write failed:", e?.message || e);
    }
  };

  // Convert a USD amount to the active currency
  const convert = (usd) => {
    const rate = data.rates?.[currency.code] || 1;
    return Number(usd || 0) * rate;
  };

  // Format with currency symbol and appropriate decimals
  const format = (usd) => {
    if (usd === null || usd === undefined || isNaN(Number(usd))) return "";
    const val = convert(usd);
    // No decimals for INR / JPY (whole numbers in their convention)
    const noDecimal = ["INR", "JPY", "AED"].includes(currency.code);
    return `${currency.symbol}${val.toLocaleString("en-US", {
      minimumFractionDigits: noDecimal ? 0 : 0,
      maximumFractionDigits: noDecimal ? 0 : 2,
    })}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, currencies: data.currencies, rates: data.rates, format, convert }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

/**
 * <Price usd={458} /> → renders the value formatted in the user's active currency, with USD as a tooltip.
 */
export function Price({ usd, className = "", showCode = false }) {
  const { format, currency } = useCurrency();
  return (
    <span className={className} title={`$${Number(usd || 0).toLocaleString("en-US")} USD`} data-testid="price-display">
      {format(usd)}
      {showCode && <span className="text-xs opacity-65 ml-1">{currency.code}</span>}
    </span>
  );
}
